import { ProjectOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client/react';
import { Button, Descriptions } from 'antd';
import clsx from 'clsx';
import * as eva from 'eva-icons';
import { forEach, nth, slice } from 'lodash';
import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import { useMedia } from 'react-use';
import { AppContext } from '../../AppContext';
import {
  AddButton,
  DeleteButton,
  EditButton,
  Export,
  Import,
} from '../../assets/svg';
import useLazyQueryWithCallbacks from '../../common/CommonUseLazyQuery';
import useQueryWithCallbacks from '../../common/CommonUseQuery';
import {
  BREAKPOINTS,
  CSV_SAMPLE_FILES,
  DEFAULTDATETIMEFORMAT,
  DEFAULT_PAGE_SIZE,
  GA_EVENT,
  GA_LABEL,
  ROUTES,
} from '../../common/constants';
import { getValidToken } from '../../common/tokenManager';
import { Event } from '../../common/trackEvents';
import useRouter from '../../common/useRouter';
import { titleCase } from '../../common/utils';
import CommonImportModal from '../../components/CommonImportModal';
import CommonPopover from '../../components/CommonPopover';
import CommonTable from '../../components/CommonTable';
import CommonTooltip from '../../components/CommonTooltip';
import EllipsisText from '../../components/EllipsisText';
import Portal from '../../components/Portal';
import SearchComponent from '../../components/SearchComponent';
import { GET_UNASSIGN_CONTACT_LIST_COUNT } from '../projects/graphql/Queries';
import { IMPORT_USER_CSV } from '../users/graphql/Mutations';
import AddAgencyModal from './AddAgencyModal';
import ApproversListingModal from './ApproversListingModal';
import DeleteAgencyModal from './DeleteAgencyModal';
import { DELETE_AGENCY } from './graphql/Mutation';
import { AGENCY_LIST } from './graphql/Queries';

const AgenciesTable = ({
  setAgencyId,
  agencyId,
  setModalVisible,
  setTitle: setModalTitle,
}) => {
  const { getCurrentUser, getCurrentTenant, getTenantUser } =
    useContext(AppContext);
  const currentUser = getCurrentUser();
  const tenantId = getCurrentTenant() || getTenantUser()?.tenant?.id;
  const [sortedInfo, setSortedInfo] = useState({});
  const [agencyData, setAgencyData] = useState();
  const [deleteModal, setDeleteModal] = useState(false);
  const [title, setTitle] = useState('');
  const [name, setName] = useState('');
  const [mutationId, setMutationId] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [showApproverDetailsModal, setShowApproverDetailsModal] =
    useState(false);
  const { navigate } = useRouter();
  const sampleFileUrl = CSV_SAMPLE_FILES.USER_CSV;
  const list = [
    'Team Name',
    'Team Type',
    'User Name',
    'Unique Email Address',
    'Unique Mobile Number',
  ];
  useEffect(() => {
    eva.replace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const initialAgencyFilter = {
    skip: 0,
    limit: 10,
  };

  const initialPaginationValue = {
    total: 0,
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  };

  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);

  const [showModal, setShowModal] = useState(false);
  const [agencyFilter, setAgencyFilter] = useState(initialAgencyFilter);
  const isHdViewport = useMedia(`(min-width: ${BREAKPOINTS.hd}px)`);
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);

  const [fetchAgencyData, { loading, data }] = useLazyQueryWithCallbacks(
    AGENCY_LIST,
    {
      variables: { filter: agencyFilter },
      fetchPolicy: 'network-only',
      onCompleted: (res) => {
        setAgencyId(res?.agencyList?.data?.[0]?.id);
        const pagination = {
          ...paginationProp,
          total: res?.agencyList?.total,
        };
        setPaginationProp(pagination);
      },
      onError() {},
    },
  );

  const { data: unassignedUserList } = useQueryWithCallbacks(
    GET_UNASSIGN_CONTACT_LIST_COUNT,
    {
      variables: {
        filter: {
          skip: 0,
          limit: 10,
        },
      },
      fetchPolicy: 'network-only',
    },
  );

  useEffect(() => {
    fetchAgencyData({ variables: { filter: agencyFilter } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleRefetchAfterDelete = () => {
    const newSkip =
      data?.agencyList?.data?.length === 1
        ? Math.max(0, agencyFilter?.skip - paginationProp?.pageSize)
        : agencyFilter?.skip;
    setAgencyFilter({
      ...agencyFilter,
      skip: newSkip,
      sortBy: { field: 'createdAt', order: 'DESC' },
    });
    fetchAgencyData({
      variables: {
        filter: {
          ...agencyFilter,
          skip: newSkip,
          sortBy: { field: 'createdAt', order: 'DESC' },
        },
      },
    });
  };
  const [deleteAgency] = useMutation(DELETE_AGENCY, {
    onError() {},
    onCompleted(res) {
      Event(GA_EVENT.DELETE_GLOBAL_AGENCY, {
        label: GA_LABEL.DELETE_GLOBAL_AGENCY,
        // eslint-disable-next-line no-undef
        pathname: window?.location?.href,
        agency_id: mutationId,
        user_id: currentUser?.id,
        user_name: currentUser?.name,
        tenant_id: currentUser?.tenantUser?.tenant?.id,
        tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
      });
      setDeleteModal(false);
      if (res?.deleteAgency?.assignedAsApprover) {
        setShowApproverDetailsModal(true);
      } else {
        handleRefetchAfterDelete();
      }
    },
  });

  const [updateAgencyImport, { loading: importLoading }] = useMutation(
    IMPORT_USER_CSV,
    { onError() {} },
  );

  const refetchAgencyDetails = () => {
    fetchAgencyData({
      variables: {
        filter: {
          ...agencyFilter,
          sortBy: { field: 'createdAt', order: 'DESC' },
        },
      },
    });
  };

  const handleAddEditAgency = () => {
    setShowModal(true);
  };

  const handleImport = () => {
    setShowImportModal(true);
  };

  const handleTableChange = (pagination, filter, sorter) => {
    const { current } = pagination;
    const skip = (current - 1) * pagination.pageSize;
    setSortedInfo(sorter);
    setPaginationProp({ ...paginationProp, ...pagination });
    if (sorter?.column) {
      setAgencyFilter({
        ...agencyFilter,
        skip,
        limit: pagination.pageSize,
        sortBy: {
          field: sorter.field,
          order: sorter.order === 'ascend' ? 'ASC' : 'DESC',
        },
      });
      fetchAgencyData({
        variables: {
          filter: {
            ...agencyFilter,
            skip,
            limit: pagination.pageSize,
            sortBy: {
              field: sorter.field,
              order: sorter.order === 'ascend' ? 'ASC' : 'DESC',
            },
          },
        },
      });
    } else {
      setAgencyFilter({
        ...agencyFilter,
        skip,
        limit: pagination.pageSize,
        sortBy: { field: 'createdAt', order: 'DESC' },
      });
      fetchAgencyData({
        variables: {
          filter: {
            ...agencyFilter,
            skip,
            limit: pagination.pageSize,
            sortBy: { field: 'createdAt', order: 'DESC' },
          },
        },
      });
    }
  };

  const handleEdit = (e, record) => {
    e?.stopPropagation?.();
    setAgencyData(record);
    handleAddEditAgency();
  };
  const handleDelete = (e, record) => {
    e?.stopPropagation?.();
    setTitle('Team');
    setName(record?.name);
    setMutationId(record?.id);
    setDeleteModal(true);
  };
  const getAssignedProjects = (record, index) => {
    const fetchName = [];
    if (record?.projectAgencies?.length !== 0) {
      forEach(record?.projectAgencies, (item) => {
        fetchName?.push(item?.project?.name);
      });
    }
    const selectedName = nth(fetchName, 0);
    if (fetchName?.length === 1) {
      return (
        <CommonTooltip
          id={`project-tooltip-single-${index}`}
          title={selectedName}
          placement={isDesktopViewport ? 'right' : 'top'}
        >
          {selectedName}
        </CommonTooltip>
      );
    }
    if (fetchName?.length > 1) {
      return (
        <CommonTooltip
          id={`project-tooltip-multiple-${index}`}
          title={slice(fetchName, 1, fetchName?.length)?.join(', ')}
          placement={isDesktopViewport ? 'right' : 'top'}
          getPopupContainer={() =>
            // eslint-disable-next-line no-undef
            document.querySelector('.table pointer')
          }
        >
          {`${selectedName} and +${fetchName?.length - 1}`}
        </CommonTooltip>
      );
    }
    return '-';
  };
  const columns = [
    {
      title: '#',
      key: 'id',
      render: (text, record, index) => {
        return (
          <div className="d-flex align-center">
            <div>{agencyFilter?.skip + index + 1}</div>
            {record?.projectAgencies?.filter((item) => !item?.isImported)
              ?.length > 0 && (
              <CommonPopover
                id="project-based-team-popover"
                placement="right"
                classNames={{ root: 'global-agency-info' }}
                trigger={[!isDesktopViewport ? 'click' : 'hover']}
                content={
                  <div id="popover-content">
                    <h4 id="popover-title">Project-based Team</h4>
                    <Descriptions
                      id="popover-descriptions"
                      layout="horizontal"
                      colon={false}
                      column={1}
                    >
                      <Descriptions.Item label="Project name">
                        <EllipsisText
                          text={
                            record?.projectAgencies?.find(
                              (item) => !item?.isImported,
                            )?.project?.name || '-'
                          }
                        />
                      </Descriptions.Item>
                      <Descriptions.Item label="Added By">
                        <EllipsisText text={record?.creator?.name || '-'} />
                      </Descriptions.Item>
                      <Descriptions.Item label="Created At">
                        {moment(record?.createdAt)?.format(
                          DEFAULTDATETIMEFORMAT,
                        )}
                      </Descriptions.Item>
                    </Descriptions>
                    <div id="popover-note" className="mt-10 font-size-13">
                      <strong>Note:</strong> Team was added in project (Details
                      above) as a result this team is shown here.
                    </div>
                  </div>
                }
              >
                <ProjectOutlined
                  id="project-icon"
                  onClick={(e) => e.stopPropagation()}
                />
              </CommonPopover>
            )}
          </div>
        );
      },
    },
    {
      title: 'TEAM',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder: sortedInfo?.columnKey === 'name' && sortedInfo?.order,
      render: (text) => (
        <div>
          <EllipsisText className="text-break" text={text} />
        </div>
      ),
    },
    {
      title: 'TYPE',
      dataIndex: 'type',
      key: 'type',
      sorter: true,
      sortOrder: sortedInfo?.columnKey === 'type' && sortedInfo?.order,
      render: (text) => titleCase(text),
    },
    {
      title: 'ASSIGNED PROJECTS',
      dataIndex: 'projects',
      key: 'projects',
      render: (_, record, index) => getAssignedProjects(record, index),
    },
    {
      key: 'action',
      width: 50,
      render: (record, _, index) => {
        return (
          <div id={`action-icons-${index}`} className="d-flex action-icons">
            <CommonTooltip
              id={`edit-tooltip-${index}`}
              trigger="focus"
              title="Edit"
            >
              <Button
                id={`edit-btn-${index}`}
                shape="circle"
                icon={<EditButton />}
                disabled={record?.isDefault}
                className={clsx(
                  'edit-button pointer',
                  record?.isDefault && 'disabled-button',
                )}
                onClick={(e) => handleEdit(e, record)}
              />
            </CommonTooltip>
            <CommonTooltip
              id={`delete-tooltip-${index}`}
              trigger="focus"
              title="Delete"
            >
              <Button
                id={`delete-btn-${index}`}
                className={clsx(
                  'delete-button pointer',
                  record?.isDefault && 'disabled-button',
                )}
                shape="circle"
                icon={<DeleteButton />}
                disabled={record?.isDefault}
                onClick={(e) => handleDelete(e, record)}
              />
            </CommonTooltip>
          </div>
        );
      },
    },
  ];

  const onSearchChange = async (value) => {
    setAgencyFilter({ ...agencyFilter, skip: 0, search: value });
    setPaginationProp(initialPaginationValue);
    fetchAgencyData({
      variables: { filter: { ...agencyFilter, skip: 0, search: value } },
    });
  };

  return (
    <>
      {showModal && (
        <AddAgencyModal
          showModal={showModal}
          setShowModal={setShowModal}
          agencyData={agencyData}
          isUpdate={!!agencyData}
          setAgencyData={setAgencyData}
          refetchAgencyDataWithInitialValues={refetchAgencyDetails}
        />
      )}

      {deleteModal && (
        <DeleteAgencyModal
          showModal={deleteModal}
          setShowModal={setDeleteModal}
          title={title}
          name={name}
          deleteMutation={deleteAgency}
          agencyId={mutationId}
          setMutationId={setMutationId}
        />
      )}
      {showApproverDetailsModal && (
        <ApproversListingModal
          showModal={showApproverDetailsModal}
          setShowModal={setShowApproverDetailsModal}
          agencyId={mutationId}
          setMutationId={setMutationId}
          name={name}
        />
      )}
      {showImportModal && (
        <CommonImportModal
          showImportModal={showImportModal}
          setShowImportModal={setShowImportModal}
          title="Team"
          sampleFileUrl={sampleFileUrl}
          list={list}
          updateAgencyImport={updateAgencyImport}
          refetchData={fetchAgencyData}
          filePrefix="agencyCsv"
          importLoading={importLoading}
          additionalPoints={[
            'Mobile number must include country code without the "+" sign (e.g., 91XXXXXXXXXX).',
            'Email address must be valid (e.g., name@example.com).',
          ]}
        />
      )}

      <Portal portalId="sub-header">
        <h5>
          {paginationProp?.total} {paginationProp?.total > 1 ? 'Teams' : 'Team'}
        </h5>
      </Portal>
      <Portal portalId="agency-btn">
        {!isDesktopViewport && (
          <SearchComponent
            id="search-container"
            className="search-component mr-10"
            getData={onSearchChange}
          />
        )}
        <div id="agency-action-buttons" className="d-flex">
          <Button
            shape="round"
            type="primary"
            id="add-btn"
            icon={<AddButton />}
            onClick={handleAddEditAgency}
          >
            {isHdViewport && 'Add'}
          </Button>

          <Button
            id="import-agency-btn"
            shape="round"
            type="primary"
            icon={<Import />}
            onClick={handleImport}
          >
            {isHdViewport && 'Import'}
          </Button>
          <Button
            id="export-agency-btn"
            shape="round"
            icon={<Export />}
            className="export-button"
            onClick={async () => {
              const token = await getValidToken();
              const exportUrl = `${process.env.REACT_APP_REPORT_SERVER_URL}/api/v1/export-csv/agencies?access_token=Bearer ${token}&tenantId=${tenantId}`;
              Event(GA_EVENT.EXPORT_AGENCIES, {
                label: GA_LABEL.EXPORT_AGENCIES,
                // eslint-disable-next-line no-undef
                pathname: window?.location?.href,
                user_id: currentUser?.id,
                user_name: currentUser?.name,
                tenant_id: currentUser?.tenantUser?.tenant?.id,
                tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
              });
              // eslint-disable-next-line no-undef
              window.open(exportUrl, '_blank');
            }}
          >
            {isHdViewport && 'Export'}
          </Button>
        </div>
      </Portal>
      <div
        id="agency-table-controls"
        className={clsx(
          'd-flex align-center  width-percent-100',
          isDesktopViewport && unassignedUserList?.unassignedUserList?.total > 0
            ? 'justify-between'
            : 'justify-end',
        )}
      >
        {unassignedUserList?.unassignedUserList?.total > 0 && (
          <Button
            id="unassigned-users-btn"
            shape="round"
            type="primary"
            className={clsx(
              'd-flex align-center mb-10 mr-10',
              !isDesktopViewport && 'font-size-12 ml-10',
            )}
            onClick={() => {
              navigate(`${ROUTES.SETUP}${ROUTES.UNASSIGN_AGENCIES}`);
            }}
          >
            {`Click here to assign ${unassignedUserList?.unassignedUserList?.total} ${
              unassignedUserList?.unassignedUserList?.total > 1
                ? 'users'
                : 'user'
            }`}
          </Button>
        )}

        {isDesktopViewport && (
          <SearchComponent
            id="search-container"
            className="search-component mb-10 mr-10 width-percent-50"
            getData={onSearchChange}
          />
        )}
      </div>

      <div id="agencies-table" className="table pointer">
        <CommonTable
          id="agencies-common-table"
          rowClassName={(record) => {
            if (record?.id === agencyId) return 'row-dark';
          }}
          loading={loading}
          columns={columns}
          data={data?.agencyList?.data || []}
          onChange={handleTableChange}
          paginationConfig={paginationProp}
          rowKey={(obj) => obj?.key ?? obj?.id}
          onRow={(record) => {
            return {
              onClick: () => {
                if (record?.id) {
                  setAgencyId(record?.id);
                  setModalVisible(true);
                  setModalTitle(record?.name);
                }
              },
            };
          }}
        />
      </div>
    </>
  );
};

export default AgenciesTable;
